import { useEffect, useRef, useState } from 'react'

interface Props {
  value: number
  onChange: (value: number) => void
  onCommit?: (value: number) => void
  disabled?: boolean
  size?: 'sm' | 'md'
  showLabel?: boolean
  className?: string
  ariaLabel?: string
}

function clampWhole(v: number): number {
  if (Number.isNaN(v)) return 0
  const c = Math.max(0, Math.min(100, v))
  return Math.round(c)
}

function colorFor(pct: number): string {
  if (pct >= 100) return '#1B7340'
  if (pct >= 75) return '#5BA84B'
  if (pct >= 50) return '#FBA737'
  if (pct >= 25) return '#F08A3A'
  return '#ED1C24'
}

function formatPct(v: number): string {
  return `${clampWhole(v)}%`
}

export default function RockPercentSlider({
  value,
  onChange,
  onCommit,
  disabled,
  size = 'md',
  showLabel = true,
  className,
  ariaLabel = 'Percent complete',
}: Props) {
  const [internal, setInternal] = useState<number>(clampWhole(value))
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const dragging = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!dragging.current && !editing) setInternal(clampWhole(value))
  }, [value, editing])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = clampWhole(parseFloat(e.target.value))
    setInternal(v)
    onChange(v)
  }

  const commit = () => {
    dragging.current = false
    onCommit?.(internal)
  }

  const startEditing = () => {
    if (disabled) return
    setEditValue(String(internal))
    setEditing(true)
  }

  const commitEdit = () => {
    const v = clampWhole(parseFloat(editValue))
    setEditing(false)
    setInternal(v)
    onChange(v)
    onCommit?.(v)
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setEditing(false)
    }
  }

  const heightCls = size === 'sm' ? 'h-1.5' : 'h-2'
  const labelCls = size === 'sm' ? 'text-sm w-12' : 'text-sm w-14'
  const accent = colorFor(internal)

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <div className="flex-1 relative">
        <div
          className={`${heightCls} rounded-full bg-[#F0EAE3] overflow-hidden`}
          aria-hidden="true"
        >
          <div
            className={`${heightCls} rounded-full transition-[width] duration-75`}
            style={{ width: `${internal}%`, backgroundColor: accent }}
          />
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={internal}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={internal}
          onMouseDown={() => { dragging.current = true }}
          onTouchStart={() => { dragging.current = true }}
          onChange={handle}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          onBlur={commit}
          className="absolute inset-0 w-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          style={{ height: '100%' }}
        />
      </div>
      {showLabel && (
        editing ? (
          <input
            ref={inputRef}
            type="number"
            min={0}
            max={100}
            step={1}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleEditKeyDown}
            onBlur={commitEdit}
            aria-label={`${ariaLabel} (type a value 0 to 100)`}
            className={`${labelCls} text-right font-semibold tabular-nums border border-stone/40 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20`}
            style={{ color: accent }}
          />
        ) : (
          <button
            type="button"
            onClick={startEditing}
            disabled={disabled}
            aria-label={`${ariaLabel}: ${formatPct(internal)}. Click to type an exact value.`}
            className={`${labelCls} text-right font-semibold tabular-nums ${disabled ? 'cursor-default' : 'cursor-text hover:underline'}`}
            style={{ color: accent }}
          >
            {formatPct(internal)}
          </button>
        )
      )}
    </div>
  )
}
